import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import Joi from 'joi';

import {
    useCreateExternalSubscriptionMutation,
    useGetDistributorCompaniesQuery,
    useGetDistributorUsersQuery,
    useGetInvestmentFundsQuery,
    useGetRolesQuery,
    useGetShareSubcategoriesQuery,
} from 'api/main';
import { distributorSchemas, subscriptionSchemas } from 'schemas';
import {
    CognitoErrors,
    EMAIL_VALIDATION_OPTIONS,
    EURO_SYMBOL,
    InternalErrors,
    NAME_REGEX,
    ORDER_BY,
    RoleNames,
    investorClassifications,
    investorErrors,
    paymentMethods,
} from 'utils/constants';
import { parseFullName, validateSchema } from 'utils/helpers';
import { titles } from 'utils/formData';
import { texts } from 'utils/languages/fr';

import { DocumentUpload } from 'containers/tunnel/common';
import SubscriptionEmaillList from './SubscriptionEmaillList';
import {
    ActionButton,
    CancelButton,
    CustomDatePicker,
    CustomPhoneField,
    CustomSelect,
    CustomTextField,
    FullAddressForm,
    Loader,
} from 'components';
import { Box, InputAdornment, MenuItem, Typography } from '@mui/material';

const defaultForm = {
    investmentFund: '',
    shareSubcategory: '',
    investingSum: '',
    admissionFee: '',
    profile: 'PP',
    investorClassification: '',
    corporateName: '',
    title: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    currentAddress: {
        address: '',
        city: '',
        zipCode: '',
        country: 'FR',
    },
    email: '',
    addresses: [],
    distributor: '',
    distributorUser: '',
    paymentMethod: paymentMethods.CREDIT_TRANSFER,
    bsSignatureDate: '',
    bsDocument: null,
};

const baseSchema = {
    investmentFund: Joi.number().required().messages({
        'number.base': texts.SAISIR_LE_CHAMP,
    }),
    shareSubcategory: Joi.number().required().messages({
        'number.base': texts.SAISIR_LE_CHAMP,
    }),
    investorClassification: Joi.string().required().messages({
        'string.empty': texts.CHOISIR_OPTION,
    }),
    title: Joi.string().required().messages({
        'string.empty': texts.CHOISIR_OPTION,
    }),
    firstName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.SAISIR_LE_CHAMP,
        'string.min': texts.FIRST_NAME_LENGTH,
        'string.max': texts.FIRST_NAME_LENGTH,
        'string.pattern.base': texts.FIRST_NAME_PATTERN,
    }),
    lastName: Joi.string().required().trim().min(2).max(120).pattern(NAME_REGEX).messages({
        'string.empty': texts.SAISIR_LE_CHAMP,
        'string.min': texts.LAST_NAME_LENGTH,
        'string.max': texts.LAST_NAME_LENGTH,
        'string.pattern.base': texts.LAST_NAME_PATTERN,
    }),
    phoneNumber: Joi.string().required().invalid('+').messages({
        'string.empty': texts.SAISIR_LE_CHAMP,
        'any.invalid': texts.SAISIR_LE_CHAMP,
    }),
    currentAddress: {
        address: Joi.string().required().messages({
            'any.required': texts.VEUILLEZ_SAISIR_VOTRE_ADDRESS,
            'string.empty': texts.VEUILLEZ_SAISIR_VOTRE_ADDRESS,
        }),
        city: Joi.string().required().messages({
            'any.required': texts.VEUILLEZ_SAISIR_LA_VILLE,
            'string.empty': texts.VEUILLEZ_SAISIR_LA_VILLE,
        }),
        zipCode: Joi.string().required().messages({
            'any.required': texts.VEUILLEZ_SAISIR_LE_CODE_POSTAL,
            'string.empty': texts.VEUILLEZ_SAISIR_LE_CODE_POSTAL,
        }),
    },
    email: Joi.string().required().email(EMAIL_VALIDATION_OPTIONS).messages({
        'string.empty': texts.SAISIR_LE_CHAMP,
        'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
    }),
    distributor: Joi.number().required().messages({
        'number.base': texts.CHOISIR_OPTION,
    }),
    distributorUser: Joi.number().required().messages({
        'number.base': texts.CHOISIR_OPTION,
    }),
    bsSignatureDate: Joi.object().invalid(null).messages({
        'any.invalid': texts.SAISIR_LE_CHAMP,
    }),
    bsDocument: Joi.object().invalid(null).messages({
        'object.base': texts.SAISIR_LE_CHAMP,
        'any.invalid': texts.SAISIR_LE_CHAMP,
    }),
};

const renderOption = obj => (
    <MenuItem key={obj.id} value={obj.id}>
        {obj.name}
    </MenuItem>
);

const ExternalSubscription = () => {
    const dispatch = useDispatch();

    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({});
    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery({
        visible: 1,
    });
    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery(
            { 'shareCategory.investmentFund': [form.investmentFund] },
            { skip: !form.investmentFund }
        );
    const { data: { distributors = [] } = { distributors: [] }, isFetching: distributorLoader } =
        useGetDistributorCompaniesQuery(
            distributorSchemas.getDistributorList({
                pagination: false,
                conventionWithFund: [+form.investmentFund],
                orderBy: ORDER_BY.distributorCompanyName,
            }),
            { skip: !form.investmentFund }
        );
    const { data: users = [], isFetching: usersLoader } = useGetDistributorUsersQuery(
        { companyId: +form.distributor },
        { skip: !form.distributor }
    );

    const [
        createExternalSubscription,
        {
            isLoading: createLoader,
            isSuccess: createSuccessful,
            isError: createFailed,
            error: createError,
        },
    ] = useCreateExternalSubscriptionMutation();

    const selectedSubcategory = useMemo(
        () => shareSubcategories.find(sc => sc.id === +form.shareSubcategory),
        [shareSubcategories, form.shareSubcategory]
    );
    const selectedFund = useMemo(
        () => fundList.find(f => f.id === +form.investmentFund),
        [fundList, form.investmentFund]
    );
    const schema = useMemo(
        () => ({
            ...baseSchema,

            corporateName: Joi.when('profile', {
                is: 'PP',
                then: Joi.any(),
                otherwise: Joi.string().required().messages({
                    'string.empty': texts.SAISIR_LE_CHAMP,
                }),
            }).messages({
                'any.only': texts.PASSWORDS_MUST_MATCH,
            }),
            distributorUser: Joi.when('distributor', {
                is: 0,
                then: Joi.any(),
                otherwise: Joi.number().required().messages({
                    'number.base': texts.SAISIR_LE_CHAMP,
                }),
            }).messages({
                'any.only': texts.PASSWORDS_MUST_MATCH,
            }),

            admissionFee: Joi.number()
                .min(0)
                .max(selectedFund?.maximumAdmissionFee || 100)
                .required()
                .messages({
                    'number.base': texts.SAISIR_LE_CHAMP,
                    'number.min': texts.PERCENTAGE_VALUE_CUSTOM(
                        selectedFund?.maximumAdmissionFee || 100
                    ),
                    'number.max': texts.PERCENTAGE_VALUE_CUSTOM(
                        selectedFund?.maximumAdmissionFee || 100
                    ),
                }),
            investingSum: Joi.number()
                .min(selectedSubcategory?.minInvestedAmount || 0)
                .max(selectedSubcategory?.maxInvestedAmount || Infinity)
                .required()
                .messages({
                    'number.base': texts.SAISIR_LE_CHAMP,
                    'number.min': texts.SUM_RANGE,
                    'number.max': texts.SUM_RANGE,
                }),
        }),
        [selectedSubcategory, selectedFund]
    );

    const handleChange = useCallback(
        key => e => {
            setForm(prevState => ({ ...prevState, [key]: e.target.value }));
            setErrors(prevState => ({ ...prevState, [key]: '' }));
        },
        []
    );

    const handleAddressChange = useCallback(newAddress => {
        setForm(prevState => ({
            ...prevState,
            currentAddress: { ...prevState.currentAddress, ...newAddress },
        }));
        setErrors(prevState => ({ ...prevState, currentAddress: '' }));
    }, []);

    const handleDateChange = useCallback(newDate => {
        setForm(oldValue => ({ ...oldValue, bsSignatureDate: newDate }));
        setErrors(prevState => ({ ...prevState, bsSignatureDate: '' }));
    }, []);

    const handleChangeAddresses = useCallback(newAddresses => {
        setForm(prevState => ({ ...prevState, addresses: newAddresses }));
    }, []);
    const handlePhoneChange = useCallback(phone => {
        setForm(prevState => ({ ...prevState, phoneNumber: phone }));
    }, []);

    const handleFileChange = useCallback(({ target: { files } }) => {
        setForm(prevState => ({ ...prevState, bsDocument: files[0] }));
        setErrors(prevState => ({ ...prevState, bsDocument: '' }));
    }, []);
    const handleRemoveFile = useCallback(() => {
        setForm(prevState => ({ ...prevState, bsDocument: null }));
    }, []);

    const goBack = useCallback(() => {
        dispatch(push('/distributors'));
    }, [dispatch]);

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        if (valid) {
            setErrors({});
            const subscriptionRole = roles.find(r => {
                if (form.profile === 'PP') {
                    return r.name === RoleNames.PP_PROFESSIONNEL;
                } else {
                    return r.name === RoleNames.PM_PROFESSIONNEL;
                }
            });
            const categoryId = shareSubcategories.find(sc => sc.id === +form.shareSubcategory)
                .shareCategory?.id;
            const payload = subscriptionSchemas.addExternalSubscription({
                ...form,
                shareCategory: categoryId,
                role: subscriptionRole?.id,
            });
            createExternalSubscription(payload);
        } else {
            setErrors(newErrors);
        }
    }, [createExternalSubscription, form, schema, roles, shareSubcategories]);

    useEffect(() => {
        if (createFailed) {
            const details = createError?.data?.detail?.split('\n') || [];
            const altError = createError?.data['hydra:description'];

            if (
                altError === InternalErrors.SHARE_CATEGORY_NOT_FOUND ||
                altError.includes(InternalErrors.CATEGORY_UNAVAILABLE)
            ) {
                setErrors(prevState => ({
                    ...prevState,
                    shareSubcategory: texts.CATEGORY_UNAVAILABLE,
                }));
            }
            if (
                createError?.data?.code === CognitoErrors.USERNAME_EXISTS ||
                createError?.data?.detail === InternalErrors.ONGOING_SUBSCRIPTION_VIOLATION ||
                details.includes(InternalErrors.USERNAME_EXISTS) ||
                details.includes(InternalErrors.EMAIL_EXISTS) ||
                details.includes(InternalErrors.USER_ROLE_VIOLATION)
            ) {
                setErrors(prevState => ({ ...prevState, email: texts.EMAIL_UNAVAILABLE }));
            }
            if (details.includes(investorErrors.INVALID_PHONE)) {
                setErrors(prevState => ({ ...prevState, phoneNumber: texts.INVALID_PHONE }));
            }
            if (details.includes(investorErrors.FIRST_NAME_ILLEGAL_CHARACTER)) {
                setErrors(prevState => ({
                    ...prevState,
                    firstName: texts.FIELD_ILLEGAL_CHARACTER,
                }));
            }
            if (details.includes(investorErrors.LAST_NAME_ILLEGAL_CHARACTER)) {
                setErrors(prevState => ({ ...prevState, lastName: texts.FIELD_ILLEGAL_CHARACTER }));
            }
        }
    }, [createError, createFailed]);

    useEffect(() => {
        if (createSuccessful) {
            dispatch(push('/subscriptions'));
        }
    }, [createSuccessful, dispatch]);

    return (
        <Box>
            <Typography variant="h5" align="left" gutterBottom>
                {texts.EXTERNAL_SUBSCRIPTION_INTEGRATION}
            </Typography>
            <Box padding={2} margin="auto" width="420px">
                <Loader loading={fundsLoader} marginTop={1}>
                    <CustomSelect
                        fullWidth
                        label={texts.FUND}
                        value={form.investmentFund}
                        error={errors.investmentFund}
                        helperText={errors.investmentFund}
                        onChange={handleChange('investmentFund')}
                    >
                        {fundList.filter(f => f.enabled).map(renderOption)}
                    </CustomSelect>
                </Loader>
                <Loader loading={subcategoriesLoader} marginTop={1}>
                    <CustomSelect
                        fullWidth
                        disabled={!form.investmentFund}
                        label={texts.SHARE_CATEGORY}
                        value={form.shareSubcategory}
                        error={errors.shareSubcategory}
                        helperText={errors.shareSubcategory}
                        onChange={handleChange('shareSubcategory')}
                    >
                        {shareSubcategories.map(renderOption)}
                    </CustomSelect>
                </Loader>
                <CustomTextField
                    formatNumber
                    label={texts.MONTANT}
                    value={form.investingSum}
                    onChange={handleChange('investingSum')}
                    error={errors.investingSum}
                    endAdornment={<InputAdornment position="start">{EURO_SYMBOL}</InputAdornment>}
                />
                <CustomTextField
                    formatNumber
                    label={texts.DROITS_DE_ENTREE}
                    value={form.admissionFee}
                    onChange={handleChange('admissionFee')}
                    error={errors.admissionFee}
                    endAdornment={<InputAdornment position="start">%</InputAdornment>}
                />
                <CustomSelect
                    value={form.profile}
                    label={texts.PROFIL_INVESTISSEUR}
                    onChange={handleChange('profile')}
                >
                    <MenuItem value="PP">{texts.PERSONNE_PHYSIQUE}</MenuItem>
                    <MenuItem value="PM">{texts.PERSONNE_MORALE}</MenuItem>
                </CustomSelect>
                <CustomSelect
                    value={form.investorClassification}
                    label={texts.STATUT}
                    onChange={handleChange('investorClassification')}
                    error={errors.investorClassification}
                    helperText={errors.investorClassification}
                >
                    {Object.keys(investorClassifications).map(ic => (
                        <MenuItem key={ic} value={ic}>
                            {investorClassifications[ic]}
                        </MenuItem>
                    ))}
                </CustomSelect>
                {form.profile === 'PM' && (
                    <CustomTextField
                        label={texts.DENOMINATION}
                        value={form.corporateName}
                        onChange={handleChange('corporateName')}
                        error={errors.corporateName}
                    />
                )}
                <CustomSelect
                    value={form.title}
                    onChange={handleChange('title')}
                    label={texts.CIVILITE}
                    error={errors.title}
                    helperText={errors.title}
                >
                    {titles.map(t => (
                        <MenuItem key={t} value={t}>
                            {t}
                        </MenuItem>
                    ))}
                </CustomSelect>
                <CustomTextField
                    label={texts.SIGNER_FIRST_NAME}
                    value={form.firstName}
                    onChange={handleChange('firstName')}
                    error={errors.firstName}
                />
                <CustomTextField
                    label={texts.SIGNER_NAME}
                    value={form.lastName}
                    onChange={handleChange('lastName')}
                    error={errors.lastName}
                />
                <CustomPhoneField
                    fullWidth
                    hideInfoButton
                    label={texts.TELEPHONE_NUMBER}
                    value={form.phoneNumber}
                    onChange={handlePhoneChange}
                    error={errors.phoneNumber}
                />
                <FullAddressForm
                    noFixedWidth
                    address={form.currentAddress}
                    label={texts.POSTAL_ADDRESS}
                    onAddressChange={handleAddressChange}
                    errors={errors}
                />
                <CustomTextField
                    inputProps={{
                        autoCapitalize: 'off',
                        autoComplete: 'off',
                        spellCheck: 'off',
                        autoCorrect: 'off',
                    }}
                    type="email"
                    label={texts.EMAIL}
                    value={form.email}
                    onChange={handleChange('email')}
                    error={errors.email}
                />
                <SubscriptionEmaillList defaultValue={[]} onChange={handleChangeAddresses} />
                <Loader loading={distributorLoader} marginTop={1}>
                    <CustomSelect
                        value={form.distributor}
                        disabled={!form.investmentFund}
                        label={texts.PARTNERS}
                        onChange={handleChange('distributor')}
                        error={errors.distributor}
                        helperText={errors.distributor}
                    >
                        {form.investmentFund && (
                            <MenuItem value={0}>{selectedFund.sgp.name}</MenuItem>
                        )}
                        {distributors.map(({ 0: { id, corporateName } }) => (
                            <MenuItem key={id} value={id}>
                                {corporateName}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                </Loader>
                {form.distributor !== 0 && (
                    <Loader loading={usersLoader} marginTop={1}>
                        <CustomSelect
                            label={texts.CONSULTANT}
                            disabled={!form.distributor}
                            onChange={handleChange('distributorUser')}
                            value={form.distributorUser}
                            error={errors.distributorUser}
                            helperText={errors.distributorUser}
                        >
                            {users.map(u => (
                                <MenuItem key={u.id} value={u.id}>
                                    {parseFullName(u)}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Loader>
                )}
                <CustomDatePicker
                    label={texts.BS_SIGNATURE_DATE}
                    value={form.bsSignatureDate}
                    onChange={handleDateChange}
                    error={errors.bsSignatureDate}
                    showError
                />
                <DocumentUpload
                    id="bsDoc"
                    type="pdf"
                    acceptedFileTypes=".pdf"
                    ContainerProps={{ marginTop: 0 }}
                    label={texts.BS_DOC}
                    error={errors.bsDocument}
                    selectedFiles={[form.bsDocument]}
                    handleFileChange={handleFileChange}
                    removeFile={handleRemoveFile}
                />
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
                <CancelButton onClick={goBack}>{texts.CANCEL}</CancelButton>
                <ActionButton
                    loading={createLoader}
                    disabled={createLoader || rolesLoader}
                    variant="contained"
                    onClick={handleSubmit}
                >
                    {texts.SAVE}
                </ActionButton>
            </Box>
        </Box>
    );
};

export default ExternalSubscription;
